"use client";

import { Sparkles, Loader2, Undo2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Presentational controls for the "Enhance prompt" flow. State lives in
 * useImprovePrompt(); a successful rewrite replaces the composer text IN PLACE
 * (no separate preview card), so these are just the two composer buttons:
 *   - <ImproveButton>  the trigger ("✨ Enhance Prompt").
 *   - <RevertButton>   shown while an enhanced prompt is applied — restores the
 *                      user's original text.
 *
 * They are deliberately theme-light and accept `className` so each composer can
 * blend them in.
 */

// ── Trigger button ───────────────────────────────────────────────────────────

interface ImproveButtonProps {
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  /** Icon-only — used where horizontal space is tight (e.g. phone composers). */
  compact?: boolean;
  className?: string;
  title?: string;
}

export function ImproveButton({
  onClick,
  loading = false,
  disabled = false,
  compact = false,
  className,
  title = "Enhance prompt",
}: ImproveButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      aria-label="Enhance prompt"
      title={title}
      className={cn(
        "inline-flex h-8 items-center justify-center gap-1.5 rounded-full border border-orange-300/60 bg-orange-50/70 px-2.5 text-[13px] font-medium text-orange-600 transition-all duration-200 hover:bg-orange-100/80 hover:text-orange-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-orange-400/25 dark:bg-orange-500/10 dark:text-orange-300 dark:hover:bg-orange-500/15",
        compact && "w-8 px-0",
        className,
      )}
    >
      {loading ? (
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
      ) : compact ? (
        <Sparkles className="h-3.5 w-3.5 shrink-0" />
      ) : null}
      {!compact && <span>{loading ? "Enhancing…" : "✨ Enhance Prompt"}</span>}
    </button>
  );
}

// ── Revert control ───────────────────────────────────────────────────────────

interface RevertButtonProps {
  onClick: () => void;
  /** Icon-only — used where horizontal space is tight (e.g. phone composers). */
  compact?: boolean;
  className?: string;
}

/**
 * Shown while an enhanced prompt is applied to the composer. Restores the
 * user's original text so they can back out of the rewrite.
 */
export function RevertButton({
  onClick,
  compact = false,
  className,
}: RevertButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Revert to your original prompt"
      title="Revert to your original prompt"
      className={cn(
        "inline-flex h-8 items-center justify-center gap-1.5 rounded-full border border-zinc-200 px-2.5 text-[13px] font-medium text-zinc-600 transition-colors hover:bg-zinc-100 hover:text-zinc-800 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:text-zinc-300 dark:hover:bg-white/10",
        compact && "w-8 px-0",
        className,
      )}
    >
      <Undo2 className="h-3.5 w-3.5 shrink-0" />
      {!compact && <span>Revert</span>}
    </button>
  );
}
